import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { USERAXIOS } from "../../api";
import { EducationalInstitution } from "../../types/resume";

interface EducationalInstitutionsResponse {
  data: EducationalInstitution[];
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

export const useEducationalInstitutions = () => {
  return useQuery({
    queryKey: ["educationalInstitutions"],
    queryFn: async (): Promise<EducationalInstitution[]> => {
      const response = await USERAXIOS.get<EducationalInstitutionsResponse>(
        "/educational_institutions"
      );
      return response.data.data;
    },
  });
};

export const useEducationalInstitutionsForAutocomplete = (
  pageSize: number = 20,
  searchTerm: string = ""
) => {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
    refetch,
  } = useInfiniteQuery({
    queryKey: ["educationalInstitutions", "infinite", pageSize, searchTerm],
    queryFn: async ({ pageParam = 1 }): Promise<EducationalInstitutionsResponse> => {
      const response = await USERAXIOS.get<EducationalInstitutionsResponse>(`/educational_institutions`, {
        params: {
          page: pageParam,
          per_page: pageSize,
          title: searchTerm || undefined,
        },
      });
      return response.data;
    },
    getNextPageParam: (lastPage) => {
      return lastPage.meta.current_page < lastPage.meta.last_page
        ? lastPage.meta.current_page + 1
        : undefined;
    },
    initialPageParam: 1,
  });

  // Flatten all pages into a single array
  const educationalInstitutions = useMemo(() => {
    return data?.pages.flatMap((page) => page.data) || [];
  }, [data]);

  const loadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  return {
    items: educationalInstitutions,
    loading: isLoading || isFetchingNextPage,
    hasMore: hasNextPage,
    loadMore,
    error,
    refetch,
    totalPages: data?.pages[0]?.meta.last_page || 0,
    totalItems: data?.pages[0]?.meta.total || 0,
  };
};
